import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { setToken } from "@/lib/api";
import { Loader2, AlertTriangle } from "lucide-react";

const ERRORS = {
  auth_failed: "Microsoft sign-in failed. Please try again.",
  tenant_not_allowed: "That Microsoft account isn't part of this organization.",
  no_email: "Your Microsoft account didn't return an email address.",
};

export default function MicrosoftCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(window.location.hash.slice(1));
    const token = params.get("token");
    const err = params.get("error");
    if (err) { setError(ERRORS[err] || "Sign-in failed."); return; }
    if (!token) { setError("No sign-in token received."); return; }
    setToken(token);
    window.history.replaceState(null, "", window.location.pathname);
    refreshUser().then(() => navigate("/", { replace: true }))
      .catch(() => setError("Could not load your profile. Please try signing in again."));
  }, [navigate, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]" data-testid="ms-callback-page">
      {error ? (
        <div className="text-center max-w-sm px-6" data-testid="ms-callback-error">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <p className="text-gray-700 font-semibold">{error}</p>
          <button onClick={() => navigate("/login", { replace: true })}
            className="mt-5 bg-[#002FA7] hover:bg-[#002480] text-white px-5 py-2 rounded-sm font-semibold text-sm">
            Back to sign in
          </button>
        </div>
      ) : (
        <div className="text-center text-gray-500" data-testid="ms-callback-loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-[#002FA7]" />
          Signing you in…
        </div>
      )}
    </div>
  );
}
